/**
 * My Submissions Page
 * 
 * Shows all submissions created by the current user.
 * Allows users to track the status of their submitted ideas.
 * Now backed by Supabase.
 */

import { useState, useEffect, useMemo } from 'react';
import { Inbox, Clock, CheckCircle, AlertCircle, Filter, X, LogIn, Route, Loader2, Radio, CheckCircle2, MinusCircle, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { Link } from 'react-router-dom';
import { useUserStore } from '../../stores/prioritization/userStore';
import { roleDefinitions } from '../../types/prioritization/roles';
import { getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { requireAuthUser } from '../../lib/requireAuthUser';
import { 
  SubmissionStatusCard, 
  SubmissionStatusFilter,
  type SubmissionStatus,
} from '../../components/prioritization/SubmissionStatusCard';
import { useStakeholderPulseStore } from '../../stores/prioritization/stakeholderPulseStore';
import {
  PULSE_OUTCOME_LABELS,
  type StakeholderPulse,
  type PulseResponse,
} from '../../types/prioritization/stakeholderPulse';
import { getWorkspaceModules, isModuleEnabled } from '../../lib/workspaceModulesService';

// Map lifecycle state/stage plus triage outcome to submitter-facing status.
function mapLifecycleToStatus(
  _state: string,
  _stage: string,
  _detailedState?: string,
  triageOutcome?: string,
  inItemPool?: boolean,
  aboveTheLine?: boolean,
  exitStatus?: string,
): SubmissionStatus {
  if (triageOutcome === 'needs_clarification') return 'NEEDS_CLARIFICATION';
  if (triageOutcome === 'redirect') return 'REDIRECTED';
  if (triageOutcome === 'reject' || exitStatus === 'archived') return 'REJECTED';
  if (triageOutcome === 'defer' || exitStatus === 'deferred') return 'DEFERRED';

  if (inItemPool) return aboveTheLine ? 'COMMITTED' : 'PRIORITIZED';
  if (triageOutcome === 'pursue') return 'UNDER_REVIEW';
  if (_state === 'item') return 'SUBMITTED';
  return 'SUBMITTED';
}

interface IdeaSubmission {
  submissionId: string;
  title: string;
  coreIntent: string;
  whyItMatters: string;
  submittedBy: string;
  submittedAt: string;
  status: SubmissionStatus;
  shapingItemId?: string;
  itemPoolId?: string;
  rank?: number;
  aboveTheLine?: boolean;
  relatedItemCount?: number;
}

// ── Pulse response panel ───────────────────────────────────────────────────────

function PulseResponsePanel({ userId }: { userId: string }) {
  const { pulses, fetchForCurrentUser, markViewed, recordResponse } = useStakeholderPulseStore();
  const [responding, setResponding] = useState<string | null>(null);
  const [responseNote, setResponseNote] = useState('');
  const [expanded, setExpanded] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pulseModuleEnabled, setPulseModuleEnabled] = useState(false);

  useEffect(() => {
    const workspaceId = getCurrentWorkspaceId();
    if (!workspaceId) return;
    getWorkspaceModules(workspaceId).then(({ data }) => {
      if (isModuleEnabled(data, 'stakeholder_pulse')) {
        setPulseModuleEnabled(true);
        fetchForCurrentUser(userId);
      }
    });
  }, [userId, fetchForCurrentUser]);

  const myPulses = useMemo(
    () => pulses.filter(p => p.submitterId === userId && p.status !== 'responded'),
    [pulses, userId],
  );

  if (!pulseModuleEnabled || myPulses.length === 0) return null;

  const handleOpen = (pulse: StakeholderPulse) => {
    if (pulse.status === 'sent') markViewed(pulse.id);
    setResponding(pulse.id);
    setResponseNote('');
  };

  const handleSubmit = async (pulseId: string, response: PulseResponse) => {
    setSubmitting(true);
    await recordResponse(pulseId, response, responseNote.trim() || undefined);
    setSubmitting(false);
    setResponding(null);
    setResponseNote('');
  };

  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--slate)] overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-[var(--slate-dim)] transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-[var(--text-ter)]" />
          <span className="text-sm font-semibold text-[var(--text-pri)]">
            Outcome Notifications
          </span>
          <span className="px-2 py-0.5 text-xs font-medium bg-[var(--slate-dim)] text-[var(--text-sec)] rounded-full">
            {myPulses.length}
          </span>
        </div>
        {expanded
          ? <ChevronUp className="h-4 w-4 text-[var(--text-ter)]" />
          : <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />}
      </button>
      {expanded && (
        <div className="divide-y divide-[var(--rule)] border-t border-[var(--rule)]">
          {myPulses.map(pulse => (
            <div key={pulse.id} className="px-5 py-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full mb-2 ${
                    pulse.outcome === 'above_cut_line' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' :
                    pulse.outcome === 'below_cut_line' ? 'bg-[var(--mustard-dim)] text-[var(--mustard)]' :
                                                         'bg-[var(--hover-tint)] text-[var(--text-sec)]'
                  }`}>
                    {PULSE_OUTCOME_LABELS[pulse.outcome]}
                    {pulse.rank != null && ` — Rank #${pulse.rank}`}
                  </span>
                  <p className="text-sm text-[var(--text-sec)] leading-relaxed">{pulse.outcomeSummary}</p>
                </div>
                {responding !== pulse.id && (
                  <button
                    onClick={() => handleOpen(pulse)}
                    className="flex-shrink-0 text-xs font-medium px-3 py-1.5 border border-[var(--slate)] text-[var(--text-ter)] hover:bg-[var(--slate-dim)] transition-colors"
                  >
                    Respond
                  </button>
                )}
              </div>

              {responding === pulse.id && (
                <div className="mt-4 bg-[var(--surface)] p-4 space-y-3">
                  <p className="text-sm font-medium text-[var(--text-pri)]">Did we address your concern?</p>
                  <div className="flex flex-wrap gap-2">
                    {([
                      { value: 'addressed',           label: 'Yes, addressed',       icon: <CheckCircle2 className="h-3.5 w-3.5" />, color: 'border-[var(--green)] text-[var(--green)] dark:text-[var(--text-ter)] hover:bg-[var(--green-dim)]' },
                      { value: 'partially_addressed', label: 'Partially',            icon: <MinusCircle className="h-3.5 w-3.5" />,  color: 'border-[var(--mustard)] text-[var(--mustard)] hover:bg-[var(--mustard-dim)]' },
                      { value: 'not_addressed',       label: 'Not addressed',        icon: <XCircle className="h-3.5 w-3.5" />,     color: 'border-[var(--coral)] text-[var(--coral)] hover:bg-[var(--coral-dim)]' },
                    ] as Array<{ value: PulseResponse; label: string; icon: React.ReactNode; color: string }>).map(opt => (
                      <button
                        key={opt.value}
                        disabled={submitting}
                        onClick={() => handleSubmit(pulse.id, opt.value)}
                        className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border  transition-colors disabled:opacity-50 ${opt.color}`}
                      >
                        {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : opt.icon}
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={responseNote}
                    onChange={e => setResponseNote(e.target.value)}
                    placeholder="Optional: share any additional context (not required)…"
                    rows={2}
                    className="w-full text-xs border border-[var(--rule)] px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-[var(--slate)]"
                  />
                  <button
                    onClick={() => setResponding(null)}
                    className="text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function MySubmissions() {
  const storeUserId = useUserStore((state) => state.userId);
  const storeUserName = useUserStore((state) => state.userName);
  const impersonatedUserName = useUserStore((state) => state.userName);
  const currentRole = useUserStore((state) => state.currentRole);
  const isImpersonating = useUserStore((state) => state.isImpersonating);

  const hasStoreSession = storeUserId && storeUserId !== 'unknown';
  const isSignedIn = !!(hasStoreSession || isImpersonating);
  const activeUserId = hasStoreSession ? storeUserId : null;
  
  const [submissions, setSubmissions] = useState<IdeaSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authSessionMissing, setAuthSessionMissing] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<SubmissionStatus[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  
  
  useEffect(() => {
    async function loadSubmissions() {
      if (!activeUserId) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        
        const workspaceId = getCurrentWorkspaceId();
        if (!workspaceId) {
          setSubmissions([]);
          setLoading(false);
          return;
        }
        const supabase = getSupabaseClient();
        const { user: authUser } = await requireAuthUser(supabase);
        if (!authUser) {
          console.log('[AUTH] missing on page', { page: 'MySubmissions', location: window.location.href });
          setAuthSessionMissing(true);
          setSubmissions([]);
          setLoading(false);
          return;
        }
        setAuthSessionMissing(false);

        // Query items table - workspace-scoped, filter by canonical submitter id.
        const { data, error: fetchError } = await supabase
          .from('items')
          .select('*')
          .eq('workspace_id', workspaceId)
          .eq('created_by_user_id', authUser.id)
          .order('created_at', { ascending: false });
        
        if (fetchError) {
          throw new Error(`Failed to load submissions: ${fetchError.message}`);
        }
        
        // Resolve display names for created_by_user_id (avoid "Anonymous" when we know the user)
        const createdByUserIds = [...new Set((data || []).map((row: any) => row.created_by_user_id).filter(Boolean))];
        let submitterDisplayMap: Record<string, string> = {};
        if (createdByUserIds.length > 0) {
          const { data: users } = await supabase
            .from('users')
            .select('id, name, email')
            .in('id', createdByUserIds);
          for (const u of users || []) {
            submitterDisplayMap[u.id] = u.name || u.email?.split('@')[0] || 'User';
          }
        }

        const itemIds = (data || []).map((row: any) => row.id);
        const triageByItemId: Record<string, { outcome: string }> = {};
        if (itemIds.length > 0) {
          const { data: triageRows } = await supabase
            .from('triage_assessments')
            .select('item_id, outcome, triaged_at')
            .in('item_id', itemIds)
            .order('triaged_at', { ascending: false });
          for (const row of triageRows || []) {
            if (row.item_id && !triageByItemId[row.item_id]) {
              triageByItemId[row.item_id] = { outcome: row.outcome };
            }
          }
        }
        
        // Map items to the submitter-facing submission interface.
        const mapped = (data || []).map((row: any) => {
          const rowId = typeof row.id === 'string' ? row.id : '';
          const createdByUserId = typeof row.created_by_user_id === 'string' ? row.created_by_user_id : '';
          return {
            submissionId: row.id,
            title: row.title,
            coreIntent: row.description || '',
            whyItMatters: row.description || '',
            submittedBy: createdByUserId
              ? (submitterDisplayMap[createdByUserId] || row.created_by || 'User')
              : (row.created_by || 'Anonymous'),
            submittedAt: row.created_at,
            status: mapLifecycleToStatus(
              row.lifecycle_state,
              row.lifecycle_stage,
              row.detailed_state,
              rowId ? triageByItemId[rowId]?.outcome : undefined,
              !!row.focus_list_id,
              !!row.above_the_line
            ),
            shapingItemId: row.id,
            itemPoolId: row.focus_list_id || undefined, // DB: focus_list_id -> app: itemPoolId
            rank: row.rank || undefined,
            aboveTheLine: row.above_the_line || undefined,
          };
        });
        
        setSubmissions(mapped);
      } catch (err) {
        console.error('Error loading submissions:', err);
        setError(err instanceof Error ? err.message : 'Failed to load submissions');
      } finally {
        setLoading(false);
      }
    }
    
    if (isSignedIn) {
      loadSubmissions();
    }
  }, [activeUserId, isSignedIn, isImpersonating, hasStoreSession, impersonatedUserName, storeUserName]);
  
  // Filter submissions
  const filteredSubmissions = useMemo(() => {
    if (selectedFilters.length === 0) return submissions;
    return submissions.filter(s => selectedFilters.includes(s.status));
  }, [submissions, selectedFilters]);
  
  // Count by status for stats
  const statusCounts = useMemo(() => {
    const counts: Partial<Record<SubmissionStatus, number>> = {};
    for (const s of submissions) {
      const key = s.status as SubmissionStatus;
      counts[key] = (counts[key] || 0) + 1;
    }
    return counts;
  }, [submissions]);
  
  // Group by phase for stats display
  const pending = submissions.filter(s => s.status === 'SUBMITTED' || s.status === 'UNDER_REVIEW');
  const shaping = submissions.filter(s => s.status === 'BEING_SHAPED' || s.status === 'NEEDS_CLARIFICATION');
  const redirected = submissions.filter(s => s.status === 'REDIRECTED');
  const prioritized = submissions.filter(s => 
    s.status === 'IN_PRIORITIZATION' || s.status === 'PRIORITIZED' || 
    s.status === 'COMMITTED' || s.status === 'IN_PROGRESS'
  );
  const completed = submissions.filter(s => s.status === 'COMPLETED');
  
  const toggleFilter = (status: SubmissionStatus) => {
    setSelectedFilters(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };
  
  const clearFilters = () => setSelectedFilters([]);
  
  const notSignedIn = !isSignedIn;

  return (
    <div className="space-y-6">
      {/* Impersonation Notice */}
      {isImpersonating && (
        <div className="flex items-center gap-3 px-4 py-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
          <div className="w-7 h-7 rounded-full bg-[var(--mustard-dim)] flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-semibold text-[var(--mustard)]">
              {impersonatedUserName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[var(--mustard)]">
              Viewing as: {impersonatedUserName}
              <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-[var(--mustard-dim)] text-[var(--mustard)]">
                {currentRole ? (roleDefinitions[currentRole]?.name || currentRole) : 'Unknown role'}
              </span>
            </p>
            <p className="text-xs text-[var(--mustard)]">Test user mode — use the role switcher to change users</p>
          </div>
        </div>
      )}

      <PageHeader
        icon={<Inbox className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="My Submissions"
        subtitle={!loading && submissions.length > 0
          ? `${submissions.length} submission${submissions.length !== 1 ? 's' : ''}`
          : 'Track the status of your submitted ideas and requests'}
        actions={
          <Link
            to="/prioritization/intake/submit"
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors"
          >
            Submit New Idea
          </Link>
        }
      />

      {/* Inline auth / error notices */}
      {notSignedIn && (
        <div className="flex items-start gap-3 px-4 py-4 bg-[var(--slate-dim)] border border-[var(--slate)]">
          <div className="p-1.5 bg-[var(--slate-dim)] flex-shrink-0">
            <LogIn className="h-4 w-4 text-[var(--text-ter)]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[var(--text-sec)]">Sign in to see your submissions</p>
            <p className="text-xs text-[var(--text-sec)] mt-0.5">Your submissions are tied to your account for tracking.</p>
            <Link to="/auth/login" className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 bg-[var(--slate)] text-white hover:bg-[var(--slate)] text-xs font-medium">
              <LogIn className="h-3.5 w-3.5" /> Sign In
            </Link>
          </div>
        </div>
      )}

      {authSessionMissing && !notSignedIn && (
        <div className="flex items-center gap-3 px-4 py-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
          <p className="text-sm text-[var(--mustard)] flex-1">Session expired — please sign in again to load your submissions.</p>
          <Link to="/auth/login" className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--mustard)] text-white hover:bg-[var(--mustard)] text-xs font-medium flex-shrink-0">
            <LogIn className="h-3.5 w-3.5" /> Sign In
          </Link>
        </div>
      )}

      {error && (
        <div className="px-4 py-3 bg-[var(--coral-dim)] border border-[var(--coral)]">
          <p className="text-sm font-semibold text-[var(--coral)]">Could not load submissions</p>
          <p className="text-sm text-[var(--coral)] mt-0.5">{error}</p>
        </div>
      )}

      {/* Stats strip */}
      {!notSignedIn && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
          <div className="grid grid-cols-3 sm:grid-cols-6 divide-x divide-[var(--rule)]">
            {[
              { icon: <Inbox className="h-4 w-4 text-[var(--text-ter)]" />, label: 'Total', value: submissions.length },
              { icon: <Clock className="h-4 w-4 text-[var(--mustard)]" />, label: 'Pending', value: pending.length },
              { icon: <AlertCircle className="h-4 w-4 text-[var(--text-ter)]" />, label: 'Item Work', value: shaping.length },
              { icon: <Route className="h-4 w-4 text-[var(--text-ter)]" />, label: 'Redirected', value: redirected.length },
              { icon: <CheckCircle className="h-4 w-4 text-[var(--text-ter)]" />, label: 'Prioritized', value: prioritized.length },
              { icon: <CheckCircle className="h-4 w-4 text-[var(--green)]" />, label: 'Completed', value: completed.length },
            ].map(({ icon, label, value }) => (
              <div key={label} className="flex items-center gap-2.5 px-4 py-3">
                {icon}
                <div>
                  <p className="text-xs text-[var(--text-ter)]">{label}</p>
                  {loading
                    ? <div className="h-5 w-5 bg-[var(--hover-tint)] rounded animate-pulse mt-0.5" />
                    : <p className="text-base font-semibold text-[var(--text-pri)] tabular-nums">{value}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending outcome notifications (Stakeholder Pulse) */}
      {!notSignedIn && activeUserId && (
        <PulseResponsePanel userId={activeUserId} />
      )}

      {/* Submissions list */}
      {!notSignedIn && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
          {/* List header */}
          <div className="px-4 py-3 border-b border-[var(--rule)] flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[var(--text-pri)]">
              {loading ? 'Your Submissions' : (
                <>Your Submissions
                  <span className="ml-1.5 text-[var(--text-ter)] font-normal">
                    ({filteredSubmissions.length}{selectedFilters.length > 0 ? ` of ${submissions.length}` : ''})
                  </span>
                </>
              )}
            </h2>
            {!loading && submissions.length > 0 && (
              <div className="flex items-center gap-2">
                {selectedFilters.length > 0 && (
                  <button onClick={clearFilters} className="flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-sec)]">
                    <X className="h-3 w-3" /> Clear
                  </button>
                )}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5  border transition-colors ${
                    showFilters || selectedFilters.length > 0
                      ? 'bg-primary-50 border-primary-200 text-primary-700'
                      : 'bg-[var(--bg-raised)] border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]'
                  }`}
                >
                  <Filter className="h-3.5 w-3.5" />
                  Filter
                  {selectedFilters.length > 0 && (
                    <span className="px-1.5 py-0.5 bg-primary-600 text-white rounded-full text-[10px] leading-none">
                      {selectedFilters.length}
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Filter chips — shown when filter panel is open */}
          {showFilters && (
            <div className="px-4 py-3 border-b border-[var(--rule)] bg-[var(--surface)]">
              <SubmissionStatusFilter
                selectedStatuses={selectedFilters}
                onToggle={toggleFilter}
                counts={statusCounts}
              />
            </div>
          )}

          {loading ? (
            <div className="flex items-center gap-2 py-10 justify-center text-[var(--text-ter)]">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading submissions…</span>
            </div>
          ) : submissions.length === 0 ? (
            <div className="text-center py-12 text-[var(--text-ter)]">
              <Inbox className="h-8 w-8 mx-auto mb-2 text-[var(--text-ter)]" />
              <p className="text-sm font-medium text-[var(--text-pri)]">No submissions yet</p>
              <p className="text-xs text-[var(--text-ter)] mt-1">
                <Link to="/prioritization/intake/submit" className="text-primary-600 hover:underline">
                  Submit your first idea
                </Link>
              </p>
            </div>
          ) : filteredSubmissions.length === 0 ? (
            <div className="text-center py-10 text-[var(--text-ter)]">
              <p className="text-sm">No submissions match your filters</p>
              <button onClick={clearFilters} className="text-xs text-primary-600 hover:underline mt-1">
                Clear filters
              </button>
            </div>
          ) : (
            <div className="divide-y divide-[var(--rule)]">
              {filteredSubmissions.map((submission) => (
                <SubmissionStatusCard
                  key={submission.submissionId}
                  submissionId={submission.submissionId}
                  title={submission.title}
                  description={submission.coreIntent}
                  status={submission.status}
                  submittedAt={submission.submittedAt}
                  itemId={submission.shapingItemId}
                  itemPoolId={submission.itemPoolId}
                  rank={submission.rank}
                  aboveTheLine={submission.aboveTheLine}
                  relatedItemCount={submission.relatedItemCount}
                  row
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

